import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Upload, FileText, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const CSVImportModal = ({ open, onOpenChange, onImport, existingStakeholders = [] }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setParsedData([]);
    setError('');
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    setError('');
    parseCSV(selectedFile);
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split(/\r?\n/).filter(line => line.trim());
        
        if (lines.length < 2) {
          setError('CSV file must have a header row and at least one data row');
          return;
        }

        // Parse header
        const header = lines[0].split(',').map(h => h.trim().toLowerCase());
        const nameIdx = header.findIndex(h => h === 'name');
        const roleIdx = header.findIndex(h => h === 'role');
        const emailIdx = header.findIndex(h => h === 'email');

        if (nameIdx === -1) {
          setError('CSV must have a "Name" column');
          return;
        }

        // Parse data rows
        const data = [];
        const existingEmails = new Set(existingStakeholders.map(s => s.email?.toLowerCase()).filter(Boolean));

        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values.length === 0) continue;

          const name = values[nameIdx]?.trim() || '';
          const role = roleIdx !== -1 ? values[roleIdx]?.trim() || '' : '';
          const email = emailIdx !== -1 ? values[emailIdx]?.trim() || '' : '';

          if (!name) continue; // Skip rows without name

          const isDuplicate = email && existingEmails.has(email.toLowerCase());
          
          data.push({
            name,
            role,
            email,
            isDuplicate,
            isValid: !isDuplicate
          });
        }

        if (data.length === 0) {
          setError('No valid data rows found in CSV');
          return;
        }

        setParsedData(data);
        setError('');
      } catch (err) {
        setError('Failed to parse CSV file: ' + err.message);
      }
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);
  };

  // Handle CSV values that may contain commas within quotes
  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  };

  const handleImport = async () => {
    const validData = parsedData
      .filter(row => row.isValid)
      .map(row => ({
        id: crypto.randomUUID(),
        name: row.name,
        role: row.role,
        email: row.email
      }));

    if (validData.length === 0) {
      setError('No valid entries to import');
      return;
    }

    setImporting(true);
    try {
      await onImport(validData);
      resetState();
      onOpenChange(false);
    } catch (err) {
      setError('Failed to import: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const validCount = parsedData.filter(row => row.isValid).length;
  const duplicateCount = parsedData.filter(row => row.isDuplicate).length;

  return (
    <Dialog open={open} onOpenChange={(open) => { if (!open) resetState(); onOpenChange(open); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Key Stakeholders from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file to bulk import stakeholders
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1">
          {/* Instructions */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium text-slate-700">CSV Format Instructions:</p>
            <p className="text-xs text-slate-600">
              Upload a CSV file with columns: <strong>Name</strong>, <strong>Role</strong>, <strong>Email</strong>
            </p>
            <div className="bg-white rounded border p-2 font-mono text-xs text-slate-600">
              <p>Name,Role,Email</p>
              <p>Sarah Mitchell,Project Manager,sarah.mitchell@example.com</p>
              <p>Tom Bradley,Technical Lead,tom.bradley@example.com</p>
            </div>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {!file ? (
              <div 
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-10 w-10 mx-auto text-slate-400 mb-3" />
                <p className="text-sm font-medium text-slate-700">Click to upload CSV file</p>
                <p className="text-xs text-slate-500 mt-1">or drag and drop</p>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <FileText className="h-5 w-5 text-green-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800">{file.name}</p>
                  <p className="text-xs text-green-600">{parsedData.length} rows parsed</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { resetState(); fileInputRef.current.value = ''; }}
                >
                  Change
                </Button>
              </div>
            )}
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Preview Table */}
          {parsedData.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-700">Preview</p>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-green-600">
                    <CheckCircle2 className="h-3 w-3 inline mr-1" />
                    {validCount} to import
                  </span>
                  {duplicateCount > 0 && (
                    <span className="text-amber-600">
                      <AlertCircle className="h-3 w-3 inline mr-1" />
                      {duplicateCount} duplicates (will skip)
                    </span>
                  )}
                </div>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-medium text-slate-600">Name</th>
                        <th className="text-left p-2 font-medium text-slate-600">Role</th>
                        <th className="text-left p-2 font-medium text-slate-600">Email</th>
                        <th className="text-center p-2 font-medium text-slate-600 w-20">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {parsedData.map((row, index) => (
                        <tr 
                          key={index} 
                          className={row.isDuplicate ? 'bg-amber-50' : ''}
                        >
                          <td className="p-2 text-slate-800">{row.name}</td>
                          <td className="p-2 text-slate-600">{row.role || '—'}</td>
                          <td className="p-2 text-slate-600">{row.email || '—'}</td>
                          <td className="p-2 text-center">
                            {row.isDuplicate ? (
                              <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded">
                                Duplicate
                              </span>
                            ) : (
                              <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { resetState(); onOpenChange(false); }}>
            Cancel
          </Button>
          <Button 
            onClick={handleImport} 
            disabled={validCount === 0 || importing}
          >
            {importing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Import {validCount} Stakeholder{validCount !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CSVImportModal;
